import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';

interface TeamImportDialogProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ImportResponse {
  success: boolean;
  data?: {
    createdTeams: number;
    createdMembers: number;
    skippedRows: number;
    teams: Array<{ id: string; name: string; code: string; memberCount: number }>;
    errors: Array<{ row: number; message: string }>;
  };
  error?: string;
  details?: unknown;
}

const SAMPLE_CSV = [
  'teamName,memberName,memberEmail,emoji',
  'Fenway Fanatics,Alice Smith,alice@example.com,⚾',
  'Fenway Fanatics,Bob Jones,bob@example.com,',
  'Boston Bruins Backers,Carla Lee,carla@example.com,🏒',
  'Boston Bruins Backers,Diego Park,diego@example.com,',
].join('\n');

/**
 * CSV import modal — paste or upload a CSV, preview the contents, and
 * submit. Aggregates by `teamName` server-side; this UI is a thin
 * shell that surfaces validation errors back from the endpoint.
 */
export function TeamImportDialog({ eventId, open, onOpenChange }: TeamImportDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState('');
  const [errors, setErrors] = useState<Array<{ row: number; message: string }>>([]);

  const importMutation = useMutation({
    mutationFn: async (csvContent: string): Promise<ImportResponse> => {
      // Server returns `success: false` with details on validation
      // errors; the apiClient unwraps that into a thrown Error, so we
      // surface raw fetch behaviour here to render row-level errors.
      const res = await apiClient.post<ImportResponse>(
        `/events/${eventId}/teams/import`,
        { csv: csvContent },
      );
      return res;
    },
    onSuccess: (res) => {
      if (!res.success || !res.data) return;
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      toast.success(
        `Imported ${res.data.createdTeams} team${res.data.createdTeams === 1 ? '' : 's'} (${res.data.createdMembers} member${res.data.createdMembers === 1 ? '' : 's'})`,
      );
      setCsv('');
      setErrors([]);
      onOpenChange(false);
    },
    onError: (err: Error & { details?: Array<{ row: number; message: string }> }) => {
      // The api-client throws on non-2xx with `error.message` set to
      // the response's `error` field. Server includes per-row errors
      // in `details` when validation fails — surface them inline so
      // the admin can fix the CSV in one pass.
      const detail = (err as Error & { details?: Array<{ row: number; message: string }> })
        .details;
      if (Array.isArray(detail)) {
        setErrors(detail);
      } else {
        setErrors([]);
      }
      toast.error(err?.message || 'Failed to import teams');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setCsv(text);
      setErrors([]);
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  const handleSubmit = () => {
    const trimmed = csv.trim();
    if (!trimmed) {
      toast.error('Paste or upload a CSV first');
      return;
    }
    setErrors([]);
    importMutation.mutate(trimmed);
  };

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'teams-import-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const previewRows = csv.split(/\r?\n/).slice(0, 6).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[90vw] !max-w-3xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Import Teams from CSV</DialogTitle>
              <DialogDescription>
                One row per (team, member). Multiple rows with the same{' '}
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">teamName</code>{' '}
                roll up into one team with multiple members.
              </DialogDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/30 p-4 text-sm">
            <p className="mb-2 font-medium text-foreground">Required columns</p>
            <ul className="ml-4 list-disc space-y-1 text-muted-foreground">
              <li>
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">teamName</code> —
                the team's display name
              </li>
            </ul>
            <p className="mb-2 mt-3 font-medium text-foreground">Optional columns</p>
            <ul className="ml-4 list-disc space-y-1 text-muted-foreground">
              <li>
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">memberName</code>{' '}
                — display name of one team member
              </li>
              <li>
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">memberEmail</code>{' '}
                — email used by the invitations feature
              </li>
              <li>
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">emoji</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">iconName</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">iconColor</code>{' '}
                — team visuals
              </li>
            </ul>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              icon={Download}
              onClick={handleDownloadSample}>
              Download sample CSV
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              size="sm"
              icon={FileText}
              onClick={() => fileInputRef.current?.click()}>
              Choose CSV file
            </Button>
            <p className="text-sm text-muted-foreground">…or paste below</p>
          </div>

          <textarea
            value={csv}
            onChange={(e) => {
              setCsv(e.target.value);
              setErrors([]);
            }}
            placeholder="teamName,memberName,memberEmail&#10;Fenway Fanatics,Alice Smith,alice@example.com&#10;…"
            className="h-48 w-full resize-y rounded-lg border bg-background p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary"
          />

          {previewRows.length > 0 && (
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="mb-1 text-xs font-medium text-muted-foreground">Preview</p>
              <pre className="overflow-x-auto whitespace-pre font-mono text-xs text-foreground">
                {previewRows.join('\n')}
                {csv.split(/\r?\n/).filter(Boolean).length > previewRows.length && '\n…'}
              </pre>
            </div>
          )}

          {errors.length > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="mb-2 text-sm font-medium text-destructive">
                {errors.length} row{errors.length === 1 ? '' : 's'} couldn't be imported
              </p>
              <ul className="max-h-32 space-y-1 overflow-y-auto text-xs text-destructive">
                {errors.slice(0, 20).map((e, i) => (
                  <li key={i}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
                {errors.length > 20 && (
                  <li className="italic">…and {errors.length - 20} more</li>
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={importMutation.isPending}
            disabled={!csv.trim()}
            icon={Upload}>
            Import Teams
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
